import styled, { css } from "styled-components";
import { popoverMotion } from "../../../design-system/tokens/popoverMotion";
import { spacing } from "../../../design-system/tokens/spacing";
import { typographyMixin } from "../../../design-system/tokens/typography";
import { borderRadius } from "../../../design-system/tokens/borders";
import { rawColors } from "../../../design-system/tokens/colors";
import { themedColor } from "../shared/themedColors";
import { shadows } from "../../../design-system/tokens/shadows";
import {
  controlSize,
  type ControlSizeKey,
} from "../../../design-system/tokens/controlSizes";

export const Wrap = styled.div<{ $fullWidth: boolean; $variant: DropdownVariant; $hug?: boolean }>`
  position: relative;
  width: ${({ $fullWidth }) => ($fullWidth ? "100%" : "auto")};
  min-width: ${({ $fullWidth, $variant, $hug }) =>
    $hug || $variant === "inline" || $variant === "chip"
      ? "0"
      : $fullWidth
        ? undefined
        : "160px"};
`;

/**
 * `default` — compact caption trigger + flat search row in menu.
 * `ghost` — borderless inline trigger.
 * `picker` — body-sized trigger + panel menu with bordered search field (ontology/metrics pickers).
 * `dashed` — body-sized trigger with a dashed border, for an empty mapping slot.
 * `pill` — full-width lavender pill trigger (Figma 6298:11570 comment-target picker).
 */
export type DropdownVariant = "default" | "ghost" | "inline" | "chip" | "picker" | "pill" | "dashed";

/** Figma picker surfaces: 8px radius, stroke/200, structural/0 (#FFF). */
export const pickerSurface = css`
  background-color: ${rawColors.structural.white};
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.lg};
`;

export const hideScrollbar = css`
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const Trigger = styled.button<{
  $open: boolean;
  $invalid: boolean;
  $size: ControlSizeKey;
  $compact: boolean;
  $variant: DropdownVariant;
  $fullWidth: boolean;
}>`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: ${({ $fullWidth, $variant }) =>
    $fullWidth ? "100%" : $variant === "inline" || $variant === "chip" ? "auto" : "auto"};
  min-width: ${({ $fullWidth, $variant }) =>
    $variant === "inline" || $variant === "chip"
      ? "0"
      : $fullWidth
        ? undefined
        : "160px"};
  height: ${({ $size, $variant }) =>
    $variant === "inline" || $variant === "chip" || $variant === "pill"
      ? "auto"
      : controlSize.field[$size]};
  min-height: ${({ $size, $variant }) =>
    $variant === "inline" || $variant === "chip" || $variant === "pill"
      ? "0"
      : controlSize.field[$size]};
  cursor: pointer;
  text-align: left;

  ${({ $variant, $open, $invalid }) =>
    $variant === "inline" || $variant === "chip"
      ? css`
          ${typographyMixin("caption", 3)}
          justify-content: flex-start;
          height: auto;
          min-height: 0;
          padding: 0;
          gap: ${$variant === "chip" ? spacing[1] : spacing[2]};
          background-color: transparent;
          border: none;
          border-radius: 0;
          color: ${rawColors.text.highEmphasis};
        `
      : $variant === "ghost"
        ? css`
            ${typographyMixin("subtitle", 3)}
            padding: ${spacing[2]} 0;
            background-color: transparent;
            border: none;
            border-radius: 0;
          `
        : $variant === "picker"
        ? css`
            ${typographyMixin("body", 3)}
            ${pickerSurface}
            padding: ${spacing[2]} ${spacing[3]};
            border-color: ${$invalid
              ? rawColors.accent.pink
              : rawColors.stroke[200]};
          `
        : $variant === "dashed"
        ? css`
            ${typographyMixin("body", 3)}
            padding: ${spacing[2]} ${spacing[3]};
            background-color: ${themedColor.surface};
            border: 1px dashed ${$invalid ? rawColors.accent.pink : $open ? rawColors.primary[500] : rawColors.stroke[300]};
            border-radius: ${borderRadius.lg};
          `
        : $variant === "pill"
        ? css`
            ${typographyMixin("caption", 3)}
            padding: ${spacing[2]} ${spacing[3]};
            background-color: ${rawColors.structural[100]};
            border: none;
            border-radius: ${borderRadius.md};
            color: ${rawColors.text.highEmphasis};
          `
        : css`
            ${typographyMixin("caption", 3)}
            padding: ${spacing[2]} ${spacing[3]};
            background-color: ${themedColor.surface};
            border: 1px solid
              ${$invalid
                ? rawColors.accent.pink
                : $open
                  ? rawColors.primary[500]
                  : themedColor.stroke};
            border-radius: ${borderRadius.md};
          `}

  &:focus-visible {
    outline: ${({ $variant }) =>
      $variant === "ghost" || $variant === "inline" || $variant === "chip" || $variant === "pill"
        ? `2px solid ${rawColors.primary[500]}`
        : "none"};
    outline-offset: ${({ $variant }) =>
      $variant === "ghost" || $variant === "inline" || $variant === "chip" || $variant === "pill"
        ? spacing[1]
        : "0"};
    ${({ $variant }) =>
      $variant === "default" &&
      css`
        border-color: ${rawColors.primary[500]};
      `}
    ${({ $variant, $invalid }) =>
      $variant === "picker" &&
      !$invalid &&
      css`
        border-color: ${rawColors.stroke[200]};
      `}
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

/** Small text before the chevron, pushed to the trigger's right edge. */
export const TriggerMeta = styled.span`
  flex: none;
  margin-left: auto;
  padding-left: ${spacing[2]};
  ${typographyMixin("caption", 3)}
  color: ${themedColor.textSecondary};
`;

export const TriggerRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  min-width: 0;
`;

export const TriggerAside = styled.span`
  flex: none;
  ${typographyMixin("caption", 3)}
  color: ${themedColor.textSecondary};
`;

export const TriggerLeft = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  color: ${themedColor.textSecondary};
  min-width: 0;
`;

export const TriggerLabel = styled.span<{ $isPlaceholder: boolean; $variant?: DropdownVariant }>`
  ${({ $isPlaceholder }) =>
    $isPlaceholder
      ? css`
          color: ${themedColor.textTertiary};
        `
      : css`
          color: ${themedColor.text};
        `}
  white-space: nowrap;
  ${({ $variant }) =>
    $variant === "inline" || $variant === "chip"
      ? css`
          overflow: visible;
          text-overflow: clip;
        `
      : css`
          overflow: hidden;
          text-overflow: ellipsis;
        `}
`;

export const Menu = styled.ul<{
  $variant: DropdownVariant;
  $showScrollbar: boolean;
  $closing?: boolean;
}>`
  position: fixed;
  margin: 0;
  list-style: none;
  ${({ $closing }) => popoverMotion($closing)}
  background-color: ${themedColor.surface};
  border: 1px solid ${themedColor.stroke};
  z-index: 2000;
  overflow-y: auto;

  ${({ $showScrollbar }) => !$showScrollbar && hideScrollbar}

  ${({ $variant }) =>
    $variant === "picker"
      ? css`
          ${pickerSurface}
          border-radius: ${borderRadius["2xl"]};
          padding: ${spacing[3]};
          box-shadow: ${shadows.component.dropdown};
        `
      : css`
          padding: ${spacing[2]};
          border-radius: ${borderRadius.md};
          box-shadow: 0 8px 24px rgba(89, 98, 112, 0.18);
        `}
`;

export const SearchRow = styled.li<{ $variant: DropdownVariant }>`
  ${({ $variant }) =>
    $variant === "picker"
      ? css`
          display: block;
          padding: 0;
          margin: 0 0 ${spacing[2]} 0;
        `
      : css`
          display: flex;
          align-items: center;
          gap: ${spacing[2]};
          padding: ${spacing[2]} ${spacing[3]};
          margin: -${spacing[2]} -${spacing[2]} ${spacing[1]};
          border-bottom: 1px solid ${rawColors.stroke[100]};
          position: sticky;
          top: -${spacing[2]};
          background-color: ${themedColor.surface};
          z-index: 1;
        `}
`;

export const TabsRow = styled.li`
  display: block;
  padding: 0;
  margin: 0 0 ${spacing[2]} 0;
`;

export const SearchField = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  height: 2.125rem; /* GAP: design 34px field height has no token. */
  padding: 0 ${spacing[2.5]};
  ${pickerSurface}
  border-color: ${rawColors.stroke.strong};
  border-radius: ${borderRadius[10]};
`;

export const SearchInput = styled.input<{ $variant: DropdownVariant }>`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: ${themedColor.text};

  ${({ $variant }) =>
    $variant === "picker"
      ? typographyMixin("caption", 4)
      : typographyMixin("caption", 3)}

  &::placeholder {
    color: ${themedColor.textTertiary};
  }
`;

export const EmptyState = styled.li`
  padding: ${spacing[3]};
  ${typographyMixin("caption", 3)}
  color: ${themedColor.textSecondary};
  text-align: center;
`;

export const MenuItem = styled.li<{
  $active: boolean;
  $compact: boolean;
  $variant: DropdownVariant;
  $disabled?: boolean;
  $rich?: boolean;
}>`
  display: flex;
  align-items: ${({ $rich }) => ($rich ? "flex-start" : "center")};
  justify-content: space-between;
  gap: ${spacing[2]};
  border-radius: ${borderRadius.sm};
  color: ${themedColor.text};
  cursor: pointer;

  ${({ $variant, $compact }) =>
    $variant === "picker"
      ? css`
          min-height: ${spacing[8]};
          padding: 0 ${spacing[2]};
          border-radius: ${borderRadius.lg};
          ${typographyMixin("body", 3)}
        `
      : css`
          padding: ${spacing[2]} ${spacing[3]};
          ${$compact ? typographyMixin("caption", 3) : typographyMixin("body", 3)}
        `}

  ${({ $active, $variant }) =>
    $active &&
    $variant !== "picker" &&
    css`
      background-color: ${themedColor.hover};
    `}

  &:hover {
    background-color: ${themedColor.hover};
  }

  /* Picker: the hovered or selected row is lavender at 600 (F22). */
  ${({ $variant, $active }) =>
    $variant === "picker" &&
    css`
      &:hover {
        background-color: ${rawColors.primary[100]};
        font-weight: 600;
      }
      ${$active &&
      css`
        background-color: ${rawColors.primary[100]};
        font-weight: 600;
      `}
    `}

  ${({ $disabled, $rich }) =>
    $disabled &&
    css`
      cursor: not-allowed;
      opacity: ${$rich ? 1 : 0.5};

      &:hover {
        background-color: transparent;
      }
    `}
`;

export const MenuItemLeft = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  color: ${themedColor.textSecondary};
  min-width: 0;
`;

export const MenuItemLabel = styled.span`
  color: ${themedColor.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** Label plus its detail lines, stacked, for an option that has a description, warning or reason. */
export const MenuItemBody = styled.span`
  display: flex;
  flex-direction: column;
  gap: ${spacing[0.5]};
  min-width: 0;
`;

export const MenuItemSmall = styled.span`
  ${typographyMixin("caption", 3)}
  color: ${themedColor.textSecondary};
`;

export const MenuItemNote = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  ${typographyMixin("caption", 2)}
  color: ${themedColor.text};

  svg {
    flex: none;
  }
`;

/**
 * Quiet text at a row's end (e.g. "5 values"), shown only while the row is hovered or focused.
 * `$always` (opt-in, `metaVisibility="always"`): always shown, caption 3 in the secondary text colour.
 */
export const MenuItemMeta = styled.span<{ $always?: boolean }>`
  display: none;
  flex-shrink: 0;
  margin-left: auto;
  color: ${rawColors.text.lowEmphasis};
  /* GAP: design 11px/500 has no type token. */
  font-size: 0.6875rem;
  font-weight: 500;

  ${MenuItem}:hover &,
  ${MenuItem}:focus-visible &,
  ${MenuItem}[aria-selected="true"] & {
    display: inline;
  }

  ${({ $always }) =>
    $always &&
    css`
      display: inline;
      flex: none;
      ${typographyMixin("caption", 3)}
      color: ${themedColor.textSecondary};
    `}
`;

/** A group heading above its rows: 10px bold capitals, wide tracking. */
export const GroupHeader = styled.li`
  padding: ${spacing[2]} ${spacing[3]} ${spacing[1]};
  list-style: none;
  color: ${rawColors.text.lowEmphasis};
  /* GAP: design 10px/700 + .1em tracking has no type token. */
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;
